import type * as Eden from '@eden';
import { createAction } from 'typesafe-actions';
import type { Hash } from '@util/hash';
import { getHash } from '@util/hash';
import { getUUID } from '@util/uuid';
import type { ResponseParseMethod } from './types';

export type RequestMeta = {
    url: string;
    method: Eden.HTTPMethod;
    auth: Eden.AuthenticationMethod;
    paginate: boolean;
    headers?: { [id: string]: string };
    responseParseMethod: ResponseParseMethod;
};

type AsyncMeta = {
    uuid: string;
    attempt: number;
    hash: Hash;
};

export const getRequestHash = (type: string, payload: any, meta: any) =>
    getHash(
        JSON.stringify({
            type,
            payload,
            meta: { ...meta, uuid: null, attempt: null },
        }),
    );

export type SetupRequest<I, B> = (input: I) => { body: B } & RequestMeta;

export type HandleResponse<Payload> = (response: any) => Payload;
const identity: HandleResponse<any> = (r) => r;

/* EXAMPLE

const fetchFoo = makeAsyncAction(
    'api/FETCH_FOO',
    'api/FETCH_FOO_SUCCESS',
    'api/FETCH_FOO_FAILURE',
    (foo: number) => ({
        body: { bar: { foo } },
        method: 'POST',
        auth: 'USER',
        url: '/foo/bar',
    }),
    (response) => ({ foo: response.foo }),
);

*/

export const makeAsyncAction = <
    Request extends string,
    Success extends string,
    Failure extends string,
    RequestInput,
    RequestPayload extends {} | void,
    ResponsePayload,
>(
    requestType: Request,
    successType: Success,
    failureType: Failure,
    setupRequest: SetupRequest<RequestInput, RequestPayload>,
    handleResponse: HandleResponse<ResponsePayload> = identity,
) => ({
    request: createAction(requestType, (wrapPayload) => (p: RequestInput, m: Partial<AsyncMeta> = {}) => {
        const { body, ...requestMeta } = setupRequest(p);
        const hash = getRequestHash(requestType, body, requestMeta);
        const asyncMeta = { uuid: getUUID('asyncAction'), attempt: 0, hash, ...m };
        const meta = { ...requestMeta, ...asyncMeta };

        return wrapPayload(body, meta);
    }),

    success: createAction(successType, (wrapPayload) => (p: any, m: RequestMeta & AsyncMeta) => {
        const payload = handleResponse(p);

        return wrapPayload(payload, m);
    }),

    failure: createAction(failureType, (wrapPayload) => (p: any, m: RequestMeta & AsyncMeta) => wrapPayload(p, m)),
});
